// Authentication and step-up contracts (modules-and-api.md §2.3, §5.2; TEN-33..37, TEN-45..49, R-06, R-09; decisions D18, D20, D21, D25).
// Request and response schemas shared by the Worker, the PWA and the OpenAPI document. Nothing here ever carries a hash.
import { z } from "zod";
import { EmailSchema, InstantSchema, UlidSchema } from "./primitives";
import { ROLES } from "./permissions";

/** R-09: at least 10 characters, no composition rules. */
export const PASSWORD_MIN_LENGTH = 10;
export const PASSWORD_MAX_LENGTH = 256;
export const PasswordSchema = z.string().min(PASSWORD_MIN_LENGTH).max(PASSWORD_MAX_LENGTH);

/**
 * The Control Log action enum as stored (TEN-51) — the values the `control_log.action` and `pin_protected_action.action`
 * CHECKs accept. D20 added the seven administrative actions (branch and user deactivation, PIN and password resets, changes
 * to the PIN-protected list itself); since migration 0001 they are stored under their own names. D47 added `PrivacyPublish`
 * (publishing a privacy notice or consent text; tenant migration 0002). Sprint 2 adds `PatientMerge` (PAT-65, D10b) and
 * `HandoverExit` (leaving kiosk mode, TEN-65; tenant migration 0004). Sprint 4 adds `StockTakeFinalise` (W11): finalising a
 * stock take and posting the opening stock (UC-INVENTORY-009, UC-INVENTORY-010), apart from a write-off, which stays a
 * `StockAdjustment` (UC-GOVERNANCE-005; tenant migration 0009 rebuilds both CHECKs, TC-DB-175).
 */
export const CONTROL_LOG_ACTIONS = [
  "Delete", "Void", "Discount", "PriceOverride", "CreditNote", "Refund", "StockAdjustment", "CashUpVariance", "CashUpReopen",
  "InvoiceEdit", "MedicalAidOverride", "LabChange", "PatientProhibit",
  "BranchDeactivate", "BranchReactivate", "UserDeactivate", "UserReactivate", "PinReset", "ForcePasswordReset", "PinProtectedListChange",
  "PrivacyPublish",
  "PatientMerge", "HandoverExit",
  "StockTakeFinalise",
  "Other",
] as const;
export type ControlLogAction = (typeof CONTROL_LOG_ACTIONS)[number];

/**
 * TEN-48's default PIN-protected set: every stored action except `Other`, the D20 and D47 actions included. Tenant data,
 * seeded at provisioning (BR-TENANCY-011); a tenant provisioned before an action existed has no row for it, and the step-up
 * middleware reads a missing row as required — the same default — so nothing is inserted by migration. Sprint 4's
 * `StockTakeFinalise` joins it, and since DVN's answer of 24 Oct 2026 (the Sprint 4 board's question 11) it is always required
 * (`ALWAYS_REQUIRED_ACTIONS`; TC-GOVERNANCE-135, TC-TENANCY-100).
 */
export const DEFAULT_PIN_PROTECTED_ACTIONS: readonly ControlLogAction[] = CONTROL_LOG_ACTIONS.filter((a) => a !== "Other");

/**
 * D20: the administrative step-up actions the API design added, plus Sprint 2's `PatientMerge` and `HandoverExit`. Stored
 * under their own names; always required — a PUT of the PIN-protected list that omits one is refused (BR-TENANCY-011).
 */
export const ADMIN_STEP_UP_ACTIONS = [
  "BranchDeactivate", "BranchReactivate", "UserDeactivate", "UserReactivate", "PinReset", "ForcePasswordReset", "PinProtectedListChange",
  "PatientMerge", "HandoverExit",
] as const;
export type AdminStepUpAction = (typeof ADMIN_STEP_UP_ACTIONS)[number];

/**
 * BR-TENANCY-011: the actions no organisation can take off its PIN-protected list — a PUT of the list that omits one is refused,
 * and the prompt for one stands whatever a stored row says: the administrative actions and, since DVN's answer of 24 Oct 2026 to
 * the Sprint 4 board's question 11 ("No"), finalising a stock take and posting the opening stock (`StockTakeFinalise`).
 */
export const ALWAYS_REQUIRED_ACTIONS = [...ADMIN_STEP_UP_ACTIONS, "StockTakeFinalise"] as const;
export type AlwaysRequiredAction = (typeof ALWAYS_REQUIRED_ACTIONS)[number];

/** Every action a route may declare with `stepUp(action, recordType)` and a client may request a token for. */
export const STEP_UP_ACTIONS = CONTROL_LOG_ACTIONS;
export type StepUpAction = (typeof STEP_UP_ACTIONS)[number];
export const StepUpActionSchema = z.enum(STEP_UP_ACTIONS);

export function isAdminStepUpAction(action: StepUpAction): action is AdminStepUpAction {
  return (ADMIN_STEP_UP_ACTIONS as readonly string[]).includes(action);
}

/** Whether an action is one the PIN-protected list can never switch off (BR-TENANCY-011). */
export function isAlwaysRequiredAction(action: StepUpAction): action is AlwaysRequiredAction {
  return (ALWAYS_REQUIRED_ACTIONS as readonly string[]).includes(action);
}

/** How a logical action is stored on a Control Log row: every action under its own name since D20 / migration 0001. */
export function controlLogActionOf(action: StepUpAction): ControlLogAction {
  return action;
}

// --- Control PIN rules (TEN-47, R-06, BR-IDENTITY-006) ------------------------------------------------------------------

export const PIN_ABSOLUTE_MIN_LENGTH = 4;
export const PIN_MAX_LENGTH = 6;

export type PinProblem = "not_numeric" | "too_short" | "too_long" | "trivial";

export interface PinRuleContext {
  /** Tenant `pin_min_length`, 4–6. Default 4. */
  minLength?: number | undefined;
  /** The user's Control Id — a PIN equal to it (zero-padded or not) is trivial. */
  controlId?: number | undefined;
}

const allSame = (pin: string): boolean => pin.split("").every((d) => d === pin[0]);
const isRun = (pin: string, step: 1 | -1): boolean => {
  for (let i = 1; i < pin.length; i++) if (Number(pin[i]) - Number(pin[i - 1]) !== step) return false;
  return true;
};

/** True when the digits are all identical, a strictly ascending or descending run, or the Control Id (D25 dropped the birth-year rule). */
export function isTrivialPin(pin: string, ctx: PinRuleContext = {}): boolean {
  if (!/^\d+$/.test(pin)) return false;
  if (allSame(pin) || isRun(pin, 1) || isRun(pin, -1)) return true;
  if (ctx.controlId !== undefined && (Number(pin) === ctx.controlId || pin === String(ctx.controlId).padStart(pin.length, "0"))) return true;
  return false;
}

/** The first rule a candidate PIN breaks, or null when it is acceptable. Enforced in the schema and again server-side. */
export function pinProblem(pin: string, ctx: PinRuleContext = {}): PinProblem | null {
  const min = Math.min(Math.max(ctx.minLength ?? PIN_ABSOLUTE_MIN_LENGTH, PIN_ABSOLUTE_MIN_LENGTH), PIN_MAX_LENGTH);
  if (!/^\d+$/.test(pin)) return "not_numeric";
  if (pin.length < min) return "too_short";
  if (pin.length > PIN_MAX_LENGTH) return "too_long";
  if (isTrivialPin(pin, ctx)) return "trivial";
  return null;
}

/** A PIN schema for one tenant and user. Issue `params.code` carries the PinProblem so the API can surface it verbatim. */
export function pinSchema(ctx: PinRuleContext = {}): z.ZodType<string> {
  return z.string().superRefine((pin, zctx) => {
    const problem = pinProblem(pin, ctx);
    if (problem) zctx.addIssue({ code: "custom", message: `Control PIN is ${problem.replace("_", " ")}`, params: { code: problem } });
  });
}

/** Shape-only PIN check for request bodies: numeric, 4–6 digits. The tenant minimum and the triviality rules run server-side. */
export const PinShapeSchema = z.string().regex(/^\d{4,6}$/, "4 to 6 digits");

// --- Sign-in (UC-IDENTITY-001, D21) ----------------------------------------------------------------------------------------

export const DEVICE_KINDS = ["Desktop", "Tablet", "Mobile", "Kiosk"] as const;
export type DeviceKind = (typeof DEVICE_KINDS)[number];

export const LoginRequestSchema = z.strictObject({
  email: EmailSchema,
  password: z.string().min(1).max(PASSWORD_MAX_LENGTH),
  totpCode: z.string().regex(/^\d{6}$/).optional(),
  /** D21: repeat the call with the chosen organisation when the credential matched in more than one. */
  orgId: UlidSchema.optional(),
  deviceKind: z.enum(DEVICE_KINDS).optional(),
  deviceLabel: z.string().max(120).optional(),
});
export type LoginRequest = z.infer<typeof LoginRequestSchema>;

export const UserSummarySchema = z.object({
  id: UlidSchema,
  email: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  displayName: z.string(),
  role: z.enum([...ROLES, "FranchisorAdmin"]),
  isClinician: z.boolean(),
  controlId: z.number().int(),
  /** Zero-padded to four digits (R-05). */
  controlIdDisplay: z.string(),
  homeBranchId: UlidSchema.nullable(),
});
export type UserSummary = z.infer<typeof UserSummarySchema>;

export const OrganisationChoiceSchema = z.object({ orgId: UlidSchema, legalName: z.string(), tradingName: z.string().nullable() });

export const LoginResponseSchema = z.object({
  user: UserSummarySchema.optional(),
  activeBranchId: UlidSchema.nullable().optional(),
  requiresPinSetup: z.literal(true).optional(),
  requiresTotp: z.literal(true).optional(),
  requiresOrganisationChoice: z.literal(true).optional(),
  organisations: z.array(OrganisationChoiceSchema).optional(),
});
export type LoginResponse = z.infer<typeof LoginResponseSchema>;

export const UnlockRequestSchema = z.strictObject({
  pin: z.string().min(1).max(PIN_MAX_LENGTH).optional(),
  password: z.string().min(1).max(PASSWORD_MAX_LENGTH).optional(),
}).refine((v) => (v.pin === undefined) !== (v.password === undefined), { message: "exactly one of pin or password", path: ["pin"] });

export const ActiveBranchRequestSchema = z.strictObject({ branchId: UlidSchema });

export const ChangePasswordRequestSchema = z.strictObject({
  currentPassword: z.string().min(1).max(PASSWORD_MAX_LENGTH),
  newPassword: PasswordSchema,
});

export const ChangePinRequestSchema = z.strictObject({
  currentPin: z.string().min(1).max(PIN_MAX_LENGTH).optional(),
  resetPin: z.string().min(1).max(PIN_MAX_LENGTH).optional(),
  newPin: PinShapeSchema,
});

export const PasswordResetRequestSchema = z.strictObject({ email: EmailSchema });
export const PasswordResetCompleteSchema = z.strictObject({ token: z.string().min(20).max(200), newPassword: PasswordSchema });

export const AcceptInvitationRequestSchema = z.strictObject({
  password: PasswordSchema.optional(),
  pin: PinShapeSchema.optional(),
}).refine((v) => v.password !== undefined || v.pin !== undefined, { message: "password or pin required", path: ["password"] });

export const InvitationViewSchema = z.object({
  organisation: z.object({ legalName: z.string(), tradingName: z.string().nullable() }),
  user: z.object({ firstName: z.string(), lastName: z.string(), email: z.string(), role: z.string(), controlIdDisplay: z.string() }),
  expiresAt: InstantSchema,
  passwordSet: z.boolean(),
});

// --- Step-up (UC-IDENTITY-008, §2.3) ---------------------------------------------------------------------------------------

export const StepUpRequestSchema = z.strictObject({
  controlId: z.number().int().min(1).max(9999),
  credential: z.string().min(1).max(64),
  action: StepUpActionSchema,
  recordType: z.string().min(1).max(64),
  recordId: z.string().min(1).max(64).optional(),
});
export type StepUpRequest = z.infer<typeof StepUpRequestSchema>;

export const StepUpResponseSchema = z.object({ stepUpToken: z.string(), expiresAt: InstantSchema });

/** Step-up token time to live (§2.3). */
export const STEP_UP_TOKEN_TTL_SECONDS = 120;
/** Five failures in fifteen minutes lock for fifteen minutes (TEN-36, TEN-49). */
export const LOCKOUT_THRESHOLD = 5;
export const LOCKOUT_WINDOW_MINUTES = 15;
export const LOCKOUT_DURATION_MINUTES = 15;
/** Invitation and password-reset link lifetimes (R-10, TEN-35). */
export const INVITATION_TTL_HOURS = 72;
export const PASSWORD_RESET_TTL_MINUTES = 30;

export const SessionSummarySchema = z.object({
  id: UlidSchema,
  deviceKind: z.enum(DEVICE_KINDS).nullable(),
  deviceLabel: z.string().nullable(),
  ip: z.string().nullable(),
  userAgent: z.string().nullable(),
  activeBranchId: UlidSchema.nullable(),
  createdAt: InstantSchema,
  lastSeenAt: InstantSchema,
  isLocked: z.boolean(),
  current: z.boolean(),
});
export type SessionSummary = z.infer<typeof SessionSummarySchema>;
